import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, AlertTriangle } from 'lucide-react'

interface ConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  type?: 'warning' | 'danger' | 'info'
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  type = 'warning'
}) => {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const getIconColor = () => {
    switch (type) {
      case 'danger':
        return '#ef4444' // red-500
      case 'warning':
        return '#f59e0b' // amber-500
      case 'info':
        return '#3b82f6' // blue-500
      default:
        return '#f59e0b'
    }
  }

  const getConfirmButtonStyle = () => {
    switch (type) {
      case 'danger':
        return {
          backgroundColor: '#ef4444',
          color: 'white',
          hoverColor: '#dc2626'
        }
      case 'warning':
        return {
          backgroundColor: '#f59e0b',
          color: 'white',
          hoverColor: '#d97706'
        }
      case 'info':
        return {
          backgroundColor: '#3b82f6',
          color: 'white',
          hoverColor: '#2563eb'
        }
      default:
        return {
          backgroundColor: '#f59e0b',
          color: 'white',
          hoverColor: '#d97706'
        }
    }
  }

  const getCancelButtonStyle = () => {
    return {
      backgroundColor: 'transparent',
      color: '#3b82f6', // blue-500 - mesma cor do título
      borderColor: '#3b82f6',
      hoverColor: '#2563eb' // blue-600
    }
  }

  const buttonStyle = getConfirmButtonStyle()
  const cancelButtonStyle = getCancelButtonStyle()

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-md rounded-lg shadow-xl"
            style={{
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--border-primary)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b"
                 style={{ borderColor: 'var(--border-primary)' }}>
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-full"
                     style={{ backgroundColor: `${getIconColor()}20` }}>
                  <AlertTriangle 
                    className="w-6 h-6" 
                    style={{ color: getIconColor() }}
                  />
                </div>
                <h3 className="text-lg font-semibold"
                    style={{ color: 'var(--text-primary)' }}>
                  {title}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-md transition-colors duration-200 hover:opacity-80"
                style={{ color: 'var(--text-secondary)' }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-sm leading-relaxed"
                 style={{ color: 'var(--text-secondary)' }}>
                {message}
              </p>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t"
                 style={{ borderColor: 'var(--border-primary)' }}>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 hover:opacity-80"
                style={{
                  backgroundColor: cancelButtonStyle.backgroundColor,
                  color: cancelButtonStyle.color,
                  border: `1px solid ${cancelButtonStyle.borderColor}`
                }}
              >
                {cancelText}
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 hover:opacity-90"
                style={{
                  backgroundColor: buttonStyle.backgroundColor,
                  color: buttonStyle.color
                }}
              >
                {confirmText}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ConfirmModal
